<template>
   <div class="form-wrapper" ref="el">
      <ClientOnly>
         <template #default>
            <VeeForm
               ref="form"
               v-slot="veeFormData"
               @submit="handleSubmit"
               @keydown.enter.prevent="handleSubmit()"
               :keepValues="true"
               :initial-values="initValue"
            >
               <div
                  v-for="field in schema.fields"
                  :key="field.name"
                  class="input-group"
                  :class="[
                     field.size,
                     field.type,
                     {
                        error: veeFormData?.errors[field.name],
                        'no-label': !field.label,
                        'read-only': field.readOnly,
                     },
                  ]"
                  v-show="!field.hidden"
               >
                  <label v-if="field.label" :for="field.name">{{
                     field.label
                  }}</label>

                  <button
                     v-if="shouldShowClearButton(field, veeFormData?.values)"
                     class="label clear"
                     type="button"
                     @click="
                        clearFieldValue(
                           field.name,
                           field.as === 'multi-select' ? [] : '',
                        )
                     "
                  >
                     Clear
                  </button>

                  <template v-if="field.as === 'multi-select'">
                     <MultiSelectField
                        :id="field.name"
                        :name="field.name"
                        v-bind="getFieldAttrs(field)"
                        :value="
                           field.initialValue ?? veeFormData?.values[field.name]
                        "
                        :options="field.children ?? null"
                        :dynamicOptions="field.dynamicOptions ?? null"
                        @change="handleChange"
                     />
                  </template>

                  <template v-else-if="field.as === 'basic-select'">
                     <SelectField
                        :id="field.name"
                        :name="field.name"
                        v-bind="getFieldAttrs(field)"
                        :value="
                           field.initialValue ?? veeFormData?.values[field.name]
                        "
                        :options="field.children ?? null"
                        :dynamicOptions="field.dynamicOptions ?? null"
                        @change="handleChange"
                     />
                  </template>

                  <template v-else-if="field.children?.length">
                     <VeeField
                        :as="field.as"
                        :id="field.name"
                        :name="field.name"
                        v-bind="field"
                        @change="handleChange"
                     >
                        <component
                           v-for="(child, idx) in field.children"
                           :key="`${field.name}-${idx}`"
                           :is="child.tag"
                           v-bind="getChildAttrs(child)"
                        >
                           {{ child.text }}
                        </component>
                     </VeeField>
                  </template>

                  <template v-else>
                     <VeeField
                        :as="field.as"
                        :id="field.name"
                        :name="field.name"
                        v-bind="field"
                        @change="handleChange"
                        @keyup="handleChange"
                        @search="handleChange"
                        @focus="handleFocus"
                        @blur="handleBlur"
                     />
                  </template>

                  <ErrorMessage :name="field.name" />
               </div>
               <Btn
                  v-if="showButton && !onUpdate"
                  :tabindex="-1"
                  v-bind="{
                     text: submitText,
                     action: true,
                     disabled: processing,
                  }"
               />
            </VeeForm>
         </template>
         <template #fallback>
            <div class="fallback-message">
               <p class="serif center">Loading...</p>
            </div>
         </template>
      </ClientOnly>
   </div>
</template>

<script setup lang="ts">
const emit = defineEmits(["submit", "updated", "focus", "blur"]);
const { hook, callHook } = useNuxtApp();
const { logInfo } = useDebugger();

const el = useTemplateRef("el");
const form = useTemplateRef("form");
const formValues = ref(null);

const props = defineProps({
   schema: {
      type: Object,
      default: () => ({}),
      required: true,
   },
   submitText: {
      type: String,
      default: "Submit",
      required: false,
   },
   onUpdate: {
      type: Boolean,
      default: false,
      required: false,
   },
   showButton: {
      type: Boolean,
      default: false,
      required: false,
   },
   processing: {
      type: Boolean,
      default: false,
      required: false,
   },
   initValue: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
      required: false,
   },
});

// Helper to check if clear button should show
const shouldShowClearButton = (field, values) => {
   return (
      ["multi-select", "basic-select"].includes(field.as) &&
      values?.[field.name]?.length &&
      field.clearable
   );
};

// Extract field attributes following the existing pattern
const getFieldAttrs = (field) => {
   const {
      as,
      name,
      label,
      children,
      initialValue,
      dynamicOptions,
      size,
      clearable,
      ...attrs
   } = field;
   return attrs;
};

// Extract child attributes
const getChildAttrs = (child) => {
   const { tag, text, ...childAttrs } = child;
   return childAttrs;
};

const clearFieldValue = (name, val = []) => {
   form.value?.setFieldValue(name, val);
   handleChange();
};

const handleSubmit = (values) => {
   const formVals = values ?? form.value?.getValues();
   emit("submit", formVals);
};

const handleChange = (ev, val) => {
   formValues.value = form.value?.getValues();
   if (props.onUpdate) {
      emit("updated", form.value?.getValues());
   }
};

const handleFocus = () => {
   emit("focus", true);
};

const handleBlur = () => {
   emit("blur", true);
};

// Hook for external form submission following project pattern
hook("form:submit", async (id) => {
   if (el.value?.id === id && form.value) {
      const { valid } = await form.value?.validate();
      valid && handleSubmit(form.value?.getValues());
      !valid && callHook("form:reset", id);
   }
});

const validate = async () => {
   const { valid } = await form.value?.validate();
   return valid;
};

const setErrors = (errors) => {
   form.value?.setErrors(errors);
};

const resetForm = (errors) => {
   form.value?.resetForm();
};

onMounted(() => {
   formValues.value = form.value?.getValues();
});

defineExpose({
   handleSubmit: handleSubmit ?? false,
   resetForm: resetForm ?? false,
   validate: validate ?? false,
   setErrors: setErrors ?? false,
   formValues: formValues ?? false,
});
</script>

<style lang="scss">
@import url("~/assets/scss/forms.scss");
</style>
